import { FC } from 'react';
import Typography from '@mui/material/Typography';
import { Control, Controller } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import { UploadType } from 'further-types/files';
import FileUpload, { FileUploadProps } from 'components/new/FileUpload';
import { BulletinUpdateFormValues } from '../schema';

type AttachmentFormValue = BulletinUpdateFormValues['attachments'][number];

const mapAttachmentsToUploadFiles = (
  attachments: Array<AttachmentFormValue>,
): FileUploadProps['files'] =>
  attachments.map((attachment) => ({
    fileName: attachment.name,
    filePath: attachment.path,
    previewUri: attachment.previewUrl,
  }));

const mapUploadFilesToAttachments = (
  files: FileUploadProps['files'],
  previousAttachments: Array<AttachmentFormValue>,
): Array<AttachmentFormValue> => {
  const previousByPath = new Map(
    previousAttachments
      .filter((attachment) => attachment.path !== '')
      .map((attachment) => [attachment.path, attachment]),
  );

  return files.map((file) => {
    const existing = file.filePath
      ? previousByPath.get(file.filePath)
      : undefined;

    return {
      name: file.fileName,
      path: file.filePath,
      previewUrl: file.previewUri ?? existing?.previewUrl ?? '',
      assignedInvestorIds: existing?.assignedInvestorIds ?? [],
      isUploading: file.filePath === '',
    };
  });
};

type Props = {
  control: Control<BulletinUpdateFormValues>;
};

const AttachmentsUpload: FC<Props> = ({ control }) => (
  <Controller
    control={control}
    name="attachments"
    render={({
      field: { value, onChange },
      fieldState: { invalid, error },
    }) => {
      const attachments = value ?? [];

      return (
        <>
          <Column spacing="xs">
            <Typography variant="h6">Upload file(s)</Typography>
            <Typography variant="body2">
              Upload any files that you wish to attach to your update.
            </Typography>
          </Column>
          <FileUpload
            height={120}
            inputTitle="Upload file(s)"
            uploadType={UploadType.LpBulletinUpdateAttachments}
            files={mapAttachmentsToUploadFiles(attachments)}
            onChange={(files) => {
              onChange(mapUploadFilesToAttachments(files, attachments));
            }}
            onDeleteFile={(fileIndex) => {
              onChange(attachments.filter((_, index) => index !== fileIndex));
            }}
            onUploadError={(message) => {
              control.setError('attachments', { message });
            }}
            error={invalid}
            helperText={error?.message}
          />
        </>
      );
    }}
  />
);

export default AttachmentsUpload;
